// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyClient } from "@/types";
import Icon from "@/UI/atoms/Icon";
import PhoneInput from "@/UI/inputs/PhoneInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Types --------------------------------------------------------------------------------------
export type TyClientFormDetails = { client: TyClient; onDone: (saved: TyClient) => void };

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * Fixes a person's own details. **Edit only** — there is no create here: staff are added by the
 * company they work for, on its own team screen, and an admin creating people for a company that did
 * not ask for them would be answering a question nobody put.
 *
 * The phone number is editable, and this is the only form in the product where it is. It is the
 * identity and what they sign in with, and nothing in Lozicore lets a person change their own — so
 * when somebody changes their number, support does it here or it does not happen.
 *
 * Suspending is not a field on it. That is a decision with a consequence worth confirming, so it
 * lives on the row behind a confirmation rather than as a switch left flipped while editing an email.
 */
export default function ClientFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { client, onDone } = (details ?? {}) as TyClientFormDetails;

    const [name, setName] = useState(client.name);
    const [phone, setPhone] = useState<string | undefined>(client.phone);
    const [email, setEmail] = useState(client.email ?? "");
    const [notes, setNotes] = useState(client.notes ?? "");
    const [formError, setFormError] = useState<string | null>(null);

    const save = useFetch<{ client: TyClient }>(`/clients/${client._id}`, { method: "PATCH" }, false);

    useEffect(() => {
        if (!save.succeededAt || !save.data?.client) return;
        onDone?.(save.data.client);
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    const handleSubmit = () => {
        setFormError(null);
        if (!name.trim()) return setFormError(t.clients.nameRequired);

        // The country picker leaves the calling code in place, so an untouched field is "+966" alone.
        const cleanPhone = (phone ?? "").replace(/\s/g, "");
        if (cleanPhone.length < 8) return setFormError(t.auth.phoneRequired);

        save.invoke({ method: "PATCH", data: { name: name.trim(), phone: cleanPhone, email: email.trim() || null, notes: notes.trim() || null } });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.clients.editTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <TextInput value={name} onChange={setName} label={t.clients.fields.name} disabled={save.loading} />
                <PhoneInput value={phone} onChange={setPhone} label={t.clients.fields.phone} disabled={save.loading} />
                <TextInput value={email} onChange={setEmail} label={t.clients.fields.email} type="email" dir="ltr" disabled={save.loading} />
                <TextInput value={notes} onChange={setNotes} label={t.clients.fields.notes} multiline rows={2} disabled={save.loading} />
                <p className="side-form__hint">{t.clients.notesHint}</p>

                {(formError || save.error) && <p className="side-form__error">{formError ?? save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={save.loading}>
                    {save.loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
